import React, { useState, useRef, useEffect } from 'react';
import './AcademicCalendar.css';

const EVENTS = {
  '2025-11-10': [{ label: 'Phase-I Classes Starts', type: 'start', detail: 'Professional Phase-I (MBBS Program) begins at Takshashila Medical College.' }],
  '2026-01-05': [{ label: 'IA Test – 1 Starts', type: 'exam', detail: 'Internal Assessment Test – 1 commences. All Phase-I students must appear.' }],
  '2026-01-19': [{ label: 'IA-1 Result Analysis', type: 'report', detail: 'Submission deadline for IA-1 Result Analysis to the department.' }],
  '2026-01-22': [{ label: 'Progress Report-1 Dispatch', type: 'report', detail: 'Progress Report-1 dispatched to students and parents.' }],
  '2026-04-16': [{ label: 'IA Test – 2 Starts', type: 'exam', detail: 'Internal Assessment Test – 2 commences for all Phase-I students.' }],
  '2026-04-30': [{ label: 'IA-2 Result Analysis', type: 'report', detail: 'Submission deadline for IA-2 Result Analysis to the department.' }],
  '2026-05-03': [{ label: 'Progress Report-2 Dispatch', type: 'report', detail: 'Progress Report-2 dispatched to students and parents.' }],
  '2026-05-25': [{ label: 'Vacation Starts', type: 'vacation', detail: 'Summer vacation begins. College remains closed from 25 May to 31 May 2026.' }],
  '2026-05-31': [{ label: 'Vacation Ends', type: 'vacation', detail: 'Summer vacation ends. Classes resume from the next working day.' }],
  '2026-06-22': [{ label: 'IA Test – 3 Starts', type: 'exam', detail: 'Internal Assessment Test – 3 commences for all Phase-I students.' }],
  '2026-07-06': [{ label: 'IA-3 Result Analysis', type: 'report', detail: 'Submission deadline for IA-3 Result Analysis to the department.' }],
  '2026-07-08': [{ label: 'Progress Report-3 Dispatch', type: 'report', detail: 'Progress Report-3 dispatched to students and parents.' }],
  '2026-07-31': [{ label: 'Pre-University Exam Starts', type: 'exam', detail: 'Phase-I Pre-University Examination begins for all eligible Phase-I students.' }],
  '2026-08-28': [{ label: 'Last Working Day', type: 'end', detail: 'Last working day of the Phase-I academic year 2025-26.' }],
  '2026-08-31': [{ label: 'Theory Exam Commencement', type: 'university', detail: 'Phase-I University Theory Examination commences (Tentative). Exact schedule subject to university notification.' }],
  '2026-09-15': [{ label: 'Practical Exam Commencement', type: 'university', detail: 'Phase-I University Practical Examination commences (Tentative). Exact schedule subject to university notification.' }],
  '2026-09-28': [{ label: 'Phase-II Classes Start', type: 'start', detail: 'Professional Phase-II (MBBS Program) commences at Takshashila Medical College.' }],
};

const TYPE_LABELS = {
  start: 'Phase Milestone',
  end: 'Phase Milestone',
  exam: 'Assessment / Exam',
  report: 'Progress Report',
  vacation: 'Vacation',
  university: 'University Exam',
};

const MONTHS = [
  { year: 2025, month: 10 },
  { year: 2025, month: 11 },
  { year: 2026, month: 0  },
  { year: 2026, month: 1  },
  { year: 2026, month: 2  },
  { year: 2026, month: 3  },
  { year: 2026, month: 4  },
  { year: 2026, month: 5  },
  { year: 2026, month: 6  },
  { year: 2026, month: 7  },
  { year: 2026, month: 8  },
];

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const milestones = [
  { label: 'Phase-I Classes Start', date: '10 Nov 2025', type: 'start' },
  { label: 'Last Working Day', date: '28 Aug 2026', type: 'end' },
  { label: 'Total Academic Days', date: '220 Days (39 Weeks)', type: 'info' },
  { label: 'Theory Exam (Tentative)', date: '31 Aug 2026', type: 'university' },
  { label: 'Practical Exam (Tentative)', date: '15 Sep 2026', type: 'university' },
  { label: 'Phase-II Classes Start', date: '28 Sep 2026', type: 'start' },
];

const DOC_PATH = '/assets/document/calendar/ACADEMIC CALENDER(2025-26)-1.pdf';

function pad(n) { return String(n).padStart(2, '0'); }
function dateKey(year, month, day) { return `${year}-${pad(month + 1)}-${pad(day)}`; }
function formatDate(year, month, day) {
  return `${pad(day)} ${MONTH_NAMES[month]} ${year}`;
}

function buildCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
}

const today = new Date();

function getInitialMonthIdx() {
  const idx = MONTHS.findIndex(m => m.year === today.getFullYear() && m.month === today.getMonth());
  return idx >= 0 ? idx : 0;
}

// Tooltip component
function EventChip({ ev, day, year, month }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState('bottom');
  const chipRef = useRef(null);
  const tooltipRef = useRef(null);

  // Flip the tooltip above the chip when there isn't room below it.
  const updatePosition = () => {
    if (!chipRef.current) return;
    const rect = chipRef.current.getBoundingClientRect();
    // Measure the real tooltip once rendered; fall back to an estimate.
    const tipHeight = tooltipRef.current?.offsetHeight || 160;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const needed = tipHeight + 16;
    // Only flip up if it actually fits up there.
    setPos(spaceBelow < needed && spaceAbove > spaceBelow ? 'top' : 'bottom');
  };

  const handleMouseEnter = () => {
    updatePosition();
    setVisible(true);
  };

  // Re-check once the tooltip has real dimensions, and on scroll/resize.
  useEffect(() => {
    if (!visible) return undefined;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <span
      ref={chipRef}
      className={`cal-event cal-event--${ev.type}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setVisible(false)}
    >
      {ev.label}
      {visible && (
        <span
          ref={tooltipRef}
          className={`cal-tooltip cal-tooltip--${pos} cal-tooltip--${ev.type}`}
        >
          <span className="cal-tooltip__type">{TYPE_LABELS[ev.type]}</span>
          <span className="cal-tooltip__date">{formatDate(year, month, day)}</span>
          <span className="cal-tooltip__title">{ev.label}</span>
          <span className="cal-tooltip__detail">{ev.detail}</span>
          <span className={`cal-tooltip__arrow cal-tooltip__arrow--${pos}`} />
        </span>
      )}
    </span>
  );
}

export default function AcademicCalendar() {
  const [monthIdx, setMonthIdx] = useState(getInitialMonthIdx);
  const { year, month } = MONTHS[monthIdx];
  const cells = buildCalendarDays(year, month);

  const isToday = (day) =>
    day && today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;

  const getEvents = (day) => {
    if (!day) return [];
    return EVENTS[dateKey(year, month, day)] || [];
  };

  return (
    <div className="ac-page">
      <div className="ac-container">

        <div className="ac-header">
          <h1 className="ac-title">Academic Calendar</h1>
          <p className="ac-subtitle">Takshashila Medical College &nbsp;|&nbsp; Professional Phase-I (MBBS Program)</p>
        </div>

        <div className="cal-card">
          <div className="cal-nav">
            <h2 className="cal-month-title">{MONTH_NAMES[month]} {year}</h2>
            <div className="cal-nav-btns">
              <button className="cal-nav-btn" onClick={() => setMonthIdx(i => Math.max(0, i - 1))} disabled={monthIdx === 0} aria-label="Previous month">&#8592;</button>
              <button className="cal-nav-btn" onClick={() => setMonthIdx(i => Math.min(MONTHS.length - 1, i + 1))} disabled={monthIdx === MONTHS.length - 1} aria-label="Next month">&#8594;</button>
            </div>
          </div>

          <div className="cal-grid cal-grid--header">
            {DAY_NAMES.map(d => <div key={d} className="cal-day-name">{d}</div>)}
          </div>

          <div className="cal-grid cal-grid--body">
            {cells.map((day, idx) => {
              const events = getEvents(day);
              return (
                <div
                  key={idx}
                  className={`cal-cell${!day ? ' cal-cell--empty' : ''}${isToday(day) ? ' cal-cell--today' : ''}${events.length > 0 ? ' cal-cell--has-event' : ''}`}
                >
                  {day && (
                    <>
                      <span className="cal-date">{day}</span>
                      {events.map((ev, ei) => (
                        <EventChip key={ei} ev={ev} day={day} year={year} month={month} />
                      ))}
                    </>
                  )}
                </div>
              );
            })}
          </div>

        </div>

        <div className="ac-doc-btn-wrap">
          <a href={`/view-document.html?file=${encodeURIComponent(DOC_PATH)}`} target="_blank" rel="noopener noreferrer" className="ac-doc-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="12" y1="18" x2="12" y2="12"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
            View Official Document
          </a>
        </div>

      </div>
    </div>
  );
}
